import type { PartnerOptInsStatus } from './SessionContext';

/**
 * The parts of a TanStack Query result that say whether its data is usable.
 * Narrow on purpose: this is derived logic, not a query wrapper.
 */
export interface PartnerOptInsQueryState {
  /** 0 until a fetch has succeeded; retained across a later failed refetch. */
  dataUpdatedAt: number;
  isLoading: boolean;
  fetchStatus: 'fetching' | 'paused' | 'idle';
}

/**
 * Works out whether the partner opt-ins are known, still coming, or unavailable.
 *
 * `isLoading` alone cannot answer this. In v4 it is true both while a request is in
 * flight and while a query is disabled and has never run, and it is false once a query
 * errors — where a defaulted empty map then looks exactly like a partner with no
 * opt-ins. `isSuccess` cannot answer it either: a failed refetch flips status to
 * 'error' while `data` still holds the last good map, so keying on it would throw away
 * an answer we already have.
 *
 * `dataUpdatedAt` is the honest signal. It is set when a fetch succeeds and survives a
 * later failure, so it means "we have a real answer" regardless of what the most recent
 * request did.
 */
export const resolvePartnerOptInsStatus = ({
  dataUpdatedAt,
  isLoading,
  fetchStatus,
}: PartnerOptInsQueryState): PartnerOptInsStatus => {
  if (dataUpdatedAt > 0) {
    return 'ready';
  }
  return isLoading && fetchStatus !== 'idle' ? 'pending' : 'unavailable';
};
